import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  UnassignedTicket,
  UnassignedTicketListResponse,
  AssignTicketRequest,
} from '../models/unassigned-ticket.model';

/**
 * API response wrapper for unassigned tickets list
 */
interface UnassignedTicketsApiResponse {
  success: boolean;
  data: UnassignedTicketListResponse;
  error?: string;
}

/**
 * API response wrapper for unassigned ticket count
 */
interface UnassignedCountApiResponse {
  success: boolean;
  data: { count: number };
  error?: string;
}

/**
 * API response wrapper for ticket assignment
 */
interface AssignTicketApiResponse {
  success: boolean;
  data: UnassignedTicket;
  error?: string;
}

/**
 * Unassigned Tickets Service
 *
 * Handles API communication for unassigned support ticket management
 * in the admin portal. Provides methods for listing, counting, and
 * assigning tickets that could not be auto-matched to an organization.
 *
 * @see agent-os/specs/2026-01-23-unassigned-tickets/spec.md
 */
@Injectable({
  providedIn: 'root',
})
export class UnassignedTicketsService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  /**
   * Get paginated list of unassigned tickets
   * GET /admin/support-tickets/unassigned
   *
   * @param query Optional pagination parameters
   * @returns Paginated list of unassigned tickets
   */
  async getUnassignedTickets(query?: {
    page?: number;
    limit?: number;
  }): Promise<UnassignedTicketListResponse> {
    let params = new HttpParams();
    if (query?.page) {
      params = params.set('page', query.page.toString());
    }
    if (query?.limit) {
      params = params.set('limit', query.limit.toString());
    }

    const response = await firstValueFrom(
      this.http.get<UnassignedTicketsApiResponse>(
        `${this.apiUrl}/admin/support-tickets/unassigned`,
        { params }
      )
    );

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch unassigned tickets');
    }

    return response.data;
  }

  /**
   * Get count of unassigned tickets
   * GET /admin/support-tickets/unassigned/count
   *
   * @returns Count of unassigned tickets
   */
  async getUnassignedCount(): Promise<number> {
    const response = await firstValueFrom(
      this.http.get<UnassignedCountApiResponse>(
        `${this.apiUrl}/admin/support-tickets/unassigned/count`
      )
    );

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch unassigned ticket count');
    }

    return response.data.count;
  }

  /**
   * Assign a ticket to an organization
   * PUT /admin/support-tickets/:ticketId/assign
   *
   * @param ticketId The ticket ID to assign
   * @param organizationId The organization ID to assign the ticket to
   * @param profileId Optional profile ID to link to a specific user
   * @returns Updated ticket data
   */
  async assignTicket(
    ticketId: string,
    organizationId: string,
    profileId?: string
  ): Promise<UnassignedTicket> {
    const body: AssignTicketRequest = { organizationId };
    if (profileId) {
      body.profileId = profileId;
    }

    const response = await firstValueFrom(
      this.http.put<AssignTicketApiResponse>(
        `${this.apiUrl}/admin/support-tickets/${ticketId}/assign`,
        body
      )
    );

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to assign ticket');
    }

    return response.data;
  }
}
